import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTransfers, type TransferKind } from "@/hooks/platform/useTransfers";
import { storageRef } from "@/lib/storageUrl";

export type MediaBucket = "event-flyers" | "product-images" | "creator-media";

const kindFor = (file: File): TransferKind => {
  if (file.type.startsWith("audio/")) return "upload-music";
  if (file.type.startsWith("video/")) return "upload-video";
  if (file.type.startsWith("image/")) return "upload-image";
  return "upload-image";
};

/**
 * Uploads to a private bucket under the user's own folder and returns a long-lived
 * signed URL that can be stored on the row and rendered directly.
 * Progress is published to the Transfers context so it shows on the Notifications page.
 */
export function useMediaUpload(bucket: MediaBucket) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const transfers = useTransfers();

  const upload = useCallback(async (file: File): Promise<string | null> => {
    setUploading(true);
    setError(null);
    const taskId = transfers.start({
      label: `Uploading ${file.name}`,
      kind: kindFor(file),
      detail: `${(file.size / 1024 / 1024).toFixed(1)} MB · ${bucket.replace("-", " ")}`,
    });
    let pct = 5;
    const ticker = window.setInterval(() => {
      pct = Math.min(92, pct + Math.random() * 12);
      transfers.update(taskId, pct);
    }, 500);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You need to be signed in to upload files.");

      const ext = file.name.split(".").pop() || "bin";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (uploadError) throw uploadError;

      // Store a permanent reference, not a signed URL — signed URLs expire
      // and leave broken images. Renderers resolve it via resolveMediaUrl.
      transfers.finish(taskId, `${file.name} uploaded`);
      return storageRef(bucket, path);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      transfers.fail(taskId, message);
      return null;
    } finally {
      window.clearInterval(ticker);
      setUploading(false);
    }
  }, [bucket, transfers]);

  return { upload, uploading, error };
}
